import React, {
  type FC,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import urlcat from "urlcat";
import { AUTH_STATUS } from "../context/enums/authEnums";
import {
  AuthContextInfo,
  AuthContextType
} from "../context/types/authContextType";
import useAuthorize from "../hooks/api/useAuthorize";
import AuthContext from "../context/authContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  KivuI18nContext,
  getLocaleFromPath,
  localizedPath,
  PAGE_ROUTE_LOGIN,
  PAGE_ROUTE_MEMBERSHIP,
  subscribeMembershipContextRefresh
} from "@kivunova/kivufrontendcommon";
import {
  isContainerPublicRoute,
  isPublicAuthRoute,
  isPublicMarketingRoute
} from "../Utils/containerPublicRoute";
import { MembershipStatus } from "../types/membership";

const initialAuthContext: AuthContextInfo = {
  auth: { status: AUTH_STATUS.UNAUTHENTICATED },
  membership: MembershipStatus.NONE
};

interface AuthProviderProps {
  children: ReactNode;
  onMembershipUpdate?: (status: MembershipStatus | null) => void;
}

const AuthProvider: FC<AuthProviderProps> = ({
  children,
  onMembershipUpdate
}) => {
  const location = useLocation();
  const [authContextInfo, updateAuthContextInfo] =
    useState<AuthContextInfo>(initialAuthContext);
  const [membershipStatus, setMembershipStatus] =
    useState<MembershipStatus | null>(null);
  const { language, defaultLang } = useContext(KivuI18nContext);
  const navigate = useNavigate();
  const useAuthorizeMutation = useAuthorize();

  // ✅ Track whether we've already done the initial auth check so we never
  // re-run it on subsequent navigations (e.g. after login navigate()).
  // The check only needs to happen once per page load to restore session.
  const hasCheckedAuth = useRef(false);
  const isAuthCheckInProgress = useRef(false);
  /** Latest auth status for the authorize effect without listing status in deps (avoids re-running authorize on unrelated context updates). */
  const authStatusRef = useRef(authContextInfo.auth.status);
  authStatusRef.current = authContextInfo.auth.status;
  const authContextInfoRef = useRef(authContextInfo);
  authContextInfoRef.current = authContextInfo;

  useEffect(() => {
    // ✅ Never run authorize on auth/marketing pages (no session expected)
    if (isContainerPublicRoute(location.pathname)) {
      return;
    }

    // ✅ Fix (Medium): Also skip if auth context already shows AUTHENTICATED.
    // This covers the case where Login.tsx completed its own authorize call and
    // set status = AUTHENTICATED before navigating to the dashboard. When the
    // pathname change triggers this effect, hasCheckedAuth.current is still false
    // (it was never set because the effect returned early on the login page), so
    // without this guard the effect would fire a redundant authorize call —
    // and if that call fails transiently, the catch block would wrongly redirect
    // the user back to login despite a successful login.
    //
    // Checking auth status here is safe: Login.tsx is the only code that sets
    // AUTHENTICATED without going through this effect's own async path, and in
    // that case it has already fully resolved membership and navigated — so there
    // is nothing left for this effect to do. We also mark hasCheckedAuth so any
    // future pathname changes (e.g. in-app navigation) remain gated correctly.
    if (authStatusRef.current === AUTH_STATUS.AUTHENTICATED) {
      hasCheckedAuth.current = true;
      return;
    }

    // ✅ Only run once per page load — subsequent navigations (like after login)
    // must NOT re-trigger authorize. Login.tsx handles post-login auth state directly.
    if (hasCheckedAuth.current) {
      return;
    }

    // ✅ Guard against concurrent calls
    if (isAuthCheckInProgress.current) {
      return;
    }

    (async () => {
      try {
        isAuthCheckInProgress.current = true;

        const { authorize } = await useAuthorizeMutation.mutateAsync();
        const { membership } = authorize;

        hasCheckedAuth.current = true;

        updateAuthContextInfo({
          auth: {
            authorized: true,
            status: AUTH_STATUS.AUTHENTICATED
          },
          membership: membership ?? undefined
        });

        setMembershipStatus(membership ?? null);
        onMembershipUpdate?.(membership ?? null);
      } catch (e) {
        console.error("Failed to authorize => navigating to login: ", e);
        hasCheckedAuth.current = true;
        const routeLocale = getLocaleFromPath(location.pathname) ?? language;
        navigate(
          urlcat(localizedPath(PAGE_ROUTE_LOGIN, routeLocale, defaultLang), {
            redirectPath: `${location.pathname}${location.search}`
          })
        );
      } finally {
        isAuthCheckInProgress.current = false;
      }
    })();

    // Pathname / locale changes only — auth status is read via authStatusRef to avoid
    // re-firing authorize when AuthContext updates without a navigation.
  }, [location.pathname, language, defaultLang]);

  // Membership MFE dispatches this after successful payment + authorize refresh
  useEffect(() => {
    return subscribeMembershipContextRefresh((detail) => {
      const membership = detail.membership as MembershipStatus;
      updateAuthContextInfo({
        ...authContextInfoRef.current,
        auth: {
          authorized: true,
          status: AUTH_STATUS.AUTHENTICATED
        },
        membership
      });
      setMembershipStatus(membership);
      onMembershipUpdate?.(membership);
    });
  }, [onMembershipUpdate]);

  // Handle membership-based redirects
  useEffect(() => {
    const isOnPublicSurface =
      isPublicMarketingRoute(location.pathname) ||
      isPublicAuthRoute(location.pathname);

    // Prefer authContextInfo.membership (set atomically by Login.tsx alongside
    // AUTHENTICATED) over local membershipStatus (page-load authorize path).
    const effectiveMembership = authContextInfo.membership ?? membershipStatus;

    // Redirect to membership whenever user does not have active membership
    // (NONE, EXPIRED, or null/unknown). Stay only when ACTIVE.
    const shouldRedirectToMembership =
      effectiveMembership !== MembershipStatus.ACTIVE;

    if (
      authContextInfo.auth.status === AUTH_STATUS.AUTHENTICATED &&
      shouldRedirectToMembership
    ) {
      const routeLocale = getLocaleFromPath(location.pathname) ?? language;
      const membershipPath = localizedPath(
        PAGE_ROUTE_MEMBERSHIP,
        routeLocale,
        defaultLang
      );
      const isOnMembershipPath =
        location.pathname.endsWith("/membership") ||
        location.pathname.includes("/membership/");

      if (!isOnMembershipPath && !isOnPublicSurface) {
        navigate(membershipPath);
      }
    }
  }, [
    authContextInfo.auth.status,
    authContextInfo.membership,
    membershipStatus,
    location.pathname,
    language,
    defaultLang
  ]);

  const authInfo = useMemo((): AuthContextType => {
    return { authContextInfo, updateAuthContextInfo };
  }, [authContextInfo]);

  return (
    <AuthContext.Provider value={authInfo}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
